import type {
    MoneyMapAccountCreateParams,
    MoneyMapApexAccountCreateParameters,
    MoneyMapApexAccountDetails,
    MoneyMapCashAccountCreateParameters,
} from '@fruitful/types';

import type {
    AccountSummary,
    BankAccountDisplayDetail,
    DepositAccount,
    PaycheckRoutingRuleAccountMetadata,
    ProposedAccount,
} from '../types/IncomeSplit';

export type IncomeSplitRuleAccount =
    | PaycheckRoutingRuleAccountMetadata
    | BankAccountDisplayDetail
    | DepositAccount
    | ProposedAccount
    | AccountSummary;

export const isBankAccountDisplayDetail = (
    account: IncomeSplitRuleAccount,
): account is BankAccountDisplayDetail => 'bankAccountId' in account;

export const getAccountSummaryName = (
    account: IncomeSplitRuleAccount,
): string | undefined => ('name' in account ? account.name : undefined);

type IncomeSplitRuleAccountStringKey =
    | 'accountNumber'
    | 'accountNumberMasked'
    | 'accountType'
    | 'bankName'
    | 'institutionName'
    | 'name'
    | 'nickname';

export const getStringAccountProperty = (
    account: IncomeSplitRuleAccount,
    key: IncomeSplitRuleAccountStringKey,
): string | null | undefined => {
    if (!(key in account)) {
        return undefined;
    }

    const value = account[key];
    return typeof value === 'string' || value == null ? value : undefined;
};

export const getAccountLogo = (
    account: IncomeSplitRuleAccount,
): string | undefined => {
    const logo = 'logo' in account ? account.logo : undefined;
    const imgBase64 = 'imgBase64' in account ? account.imgBase64 : undefined;
    return logo ?? imgBase64 ?? undefined;
};

const getLogoParts = (logo: string | null | undefined) => {
    const [icon, color] = logo?.split(':') ?? [];
    return { icon, color };
};

export const getIncomeSplitAccountVisuals = (
    account: IncomeSplitRuleAccount,
): { icon?: string | null; color?: string | null } => {
    const logoParts = getLogoParts(
        'logo' in account ? account.logo : undefined,
    );

    if (isBankAccountDisplayDetail(account)) {
        return {
            icon:
                account.accountDetails?.icon ?? account.icon ?? logoParts.icon,
            color:
                account.accountDetails?.color ??
                account.color ??
                logoParts.color,
        };
    }

    return {
        icon:
            'icon' in account
                ? (account.icon ?? logoParts.icon)
                : logoParts.icon,
        color:
            'color' in account
                ? (account.color ?? logoParts.color)
                : logoParts.color,
    };
};

export const hasCashCreateParameters = (
    createParameters: MoneyMapAccountCreateParams | undefined,
): createParameters is MoneyMapCashAccountCreateParameters =>
    createParameters != null &&
    'category' in createParameters &&
    'icon' in createParameters &&
    'color' in createParameters;

export const hasApexCreateParameters = (
    createParameters: MoneyMapAccountCreateParams | undefined,
): createParameters is MoneyMapApexAccountCreateParameters =>
    createParameters != null &&
    'apexAccountType' in createParameters &&
    'icon' in createParameters &&
    'color' in createParameters;

const isApexAccountDetails = (
    accountDetails: unknown,
): accountDetails is MoneyMapApexAccountDetails =>
    typeof accountDetails === 'object' &&
    accountDetails != null &&
    'name' in accountDetails &&
    'accountNumberMasked' in accountDetails &&
    'apexAccountType' in accountDetails;

export const getApexAccountDetails = (
    account: object,
): MoneyMapApexAccountDetails | undefined => {
    if (!('accountDetails' in account)) {
        return undefined;
    }

    return isApexAccountDetails(account.accountDetails)
        ? account.accountDetails
        : undefined;
};
